import React, { useContext } from 'react'
import styled, { css } from 'styled-components'

const RadioContext = React.createContext<{ value?: string | number; onChange(value: string | number): void }>({
  value: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: () => {},
})

type Props = {
  label?: string | React.ReactElement
  value: string | number
  disabled?: boolean
  className?: string
}

type RadioWrapperProps = Pick<Props, 'disabled'>

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;

  ${({ disabled }: RadioWrapperProps) => ({
    pointerEvents: disabled ? 'none' : 'auto',
    opacity: disabled ? '0.5' : '1',
  })};
`

const BoxWrapper = styled.button`
  display: flex;

  justify-content: center;
  align-items: center;

  box-sizing: border-box;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  padding: 0;

  border-radius: 50%;
  outline: none;
  cursor: inherit;

  &:focus {
    border-color: ${props => props.theme.color.primary600};
  }

  ${({ theme }) => ({
    border: `1px solid ${theme.color.secondary200}`,
    background: theme.color.onPrimaryLight,
  })}
`

const CheckMark = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.theme.color.primary600};
`

type BoxAreaProps = {
  isChecked?: boolean
  disabled?: boolean
  value: string | number
}

const BoxArea = ({ isChecked, disabled, value }: BoxAreaProps) => (
  <BoxWrapper type={'button'} role={'radio'} aria-checked={isChecked} title={`${value}`} disabled={disabled}>
    {isChecked ? <CheckMark /> : null}
  </BoxWrapper>
)

const Label = styled.span`
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;

  ${({ theme }) => ({
    color: theme.color.onPrimaryDark,
  })}
`

export const RadioItem: React.FC<Props> = ({ value, label, disabled, className }) => {
  const context = useContext(RadioContext)
  const isChecked = context.value === value
  const onChange = context.onChange
  return (
    <RadioWrapper disabled={!!disabled} onClick={() => !disabled && onChange(value)} className={className}>
      <BoxArea isChecked={isChecked} disabled={!!disabled} value={value} />
      {label && <Label>{label}</Label>}
    </RadioWrapper>
  )
}

const ContainerStyled = styled.div`
  ${({ direction }: { direction: 'column' | 'row' }) => {
    if (direction === 'column') {
      return css`
        display: grid;
        grid-gap: 10px;
        grid-template-columns: 1fr;
      `
    } else if (direction === 'row') {
      return css`
        display: flex;

        ${RadioWrapper} {
          &:not(:last-child) {
            margin-right: 10px;
          }
        }
      `
    } else {
      return ''
    }
  }}
`

type PropsContainer = {
  value?: string | number
  className?: string
  onChange(value: string | number): void
  direction?: 'column' | 'row'
  children?: React.ReactNode
}

export const Radio: React.FC<PropsContainer> = ({ value, onChange, className, children, direction = 'row' }) => {
  return (
    <RadioContext.Provider value={{ value, onChange }}>
      <ContainerStyled className={className} direction={direction}>
        {children}
      </ContainerStyled>
    </RadioContext.Provider>
  )
}
